import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  InputAdornment,
  Paper,
  Radio,
  RadioGroup,
  Slider,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { ModelSelector } from '../ModelSelector';
import { LearningSettings, LearningStatus, Plan, SIGNAL_DESCRIPTIONS, TargetDetail, TargetType } from '../../services/learningApi';
import { PlanSummary } from './PlanSummary';
import { HelpTip, stepLabel, useApis } from './common';

const MODEL_STAGES: { stage: keyof LearningSettings['models']; label: string; help: string }[] = [
  { stage: 'profile', label: 'Profile builder', help: 'Writes the learning profile from your instructions, schema and sample runs. Runs rarely.' },
  { stage: 'extract', label: 'Run analysis', help: 'Reads each batch of sampled runs and pulls out learnings. Most of the cost of a cycle.' },
  { stage: 'review', label: 'Review & suggestions', help: 'Combines findings into the report and suggests changes. One call per cycle.' },
];

const ADVANCED_KEY = 'learning.settings.showAdvanced';

interface Props {
  targetType: TargetType;
  targetId: string;
  detail: TargetDetail;
  status: LearningStatus;
  onSaved: () => void;
}

const Section: React.FC<{ title: string; subtitle?: string; children: React.ReactNode }> = ({ title, subtitle, children }) => (
  <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
    <Typography variant="subtitle1" fontWeight={600}>
      {title}
    </Typography>
    {subtitle && (
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {subtitle}
      </Typography>
    )}
    {children}
  </Paper>
);

const NumberField: React.FC<{
  label: string;
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  step?: number;
  helperText?: string;
  suffix?: string;
  disabled?: boolean;
}> = ({ label, value, onChange, min, max, step, helperText, suffix, disabled }) => (
  <TextField
    fullWidth
    size="small"
    type="number"
    label={label}
    value={Number.isFinite(value) ? value : ''}
    disabled={disabled}
    onChange={(e) => onChange(e.target.value === '' ? NaN : Number(e.target.value))}
    inputProps={{ min, max, step }}
    InputProps={suffix ? { endAdornment: <InputAdornment position="end">{suffix}</InputAdornment> } : undefined}
    helperText={helperText}
  />
);

function clone<T>(v: T): T {
  return JSON.parse(JSON.stringify(v));
}

export const LearningSettingsTab: React.FC<Props> = ({ targetType, targetId, detail, status, onSaved }) => {
  const apis = useApis();
  const [settings, setSettings] = useState<LearningSettings>(() => clone(detail.settings));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedMsg, setSavedMsg] = useState<string | null>(null);
  const [plan, setPlan] = useState<Plan | null>(null);
  const [planLoading, setPlanLoading] = useState(false);
  const previewSeq = useRef(0);
  const [showAdvanced, setShowAdvancedState] = useState(() => localStorage.getItem(ADVANCED_KEY) === '1');
  const setShowAdvanced = (v: boolean) => {
    setShowAdvancedState(v);
    localStorage.setItem(ADVANCED_KEY, v ? '1' : '0');
  };

  const savedJson = JSON.stringify(detail.settings);
  useEffect(() => {
    setSettings(JSON.parse(savedJson));
  }, [savedJson]);

  const dirty = useMemo(() => JSON.stringify(settings) !== savedJson, [settings, savedJson]);
  const limits = status.limits || { min_runs: 5, max_runs: 1000, min_credit_cap: 5, max_credit_cap: 5000 };
  const nodes = detail.target.nodes || [];
  const aiNodes = nodes.filter((n) => n.kind === 'ai_function');

  const update = (fn: (s: LearningSettings) => void) => {
    setSavedMsg(null);
    setSettings((prev) => {
      const next = clone(prev);
      fn(next);
      return next;
    });
  };

  // Live preview: debounce so typing in number fields doesn't spam the planner.
  useEffect(() => {
    const seq = ++previewSeq.current;
    setPlanLoading(true);
    const timer = setTimeout(() => {
      apis.learning
        .preview(targetType, targetId, settings)
        .then((p) => seq === previewSeq.current && setPlan(p))
        .catch((e) => seq === previewSeq.current && setError(e.message))
        .finally(() => seq === previewSeq.current && setPlanLoading(false));
    }, 700);
    return () => clearTimeout(timer);
  }, [apis, targetType, targetId, settings]);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await apis.learning.updateTarget(targetType, targetId, { settings });
      setSettings(clone(res.settings));
      setSavedMsg(res.warnings.length ? `Saved. ${res.warnings.join(' ')}` : 'Saved.');
      onSaved();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const capAuto = settings.budget.credit_cap === null;
  const signals = status.signal_providers || Object.keys(SIGNAL_DESCRIPTIONS);
  const nodeLabel = (id: string) => stepLabel(nodes, id);
  const profileLearn = detail.profile?.learn_nodes.length ? detail.profile.learn_nodes.map(nodeLabel).join(', ') : 'every step';
  const profileInjectNodes = Array.from(new Set((detail.profile?.inject || []).map((i) => i.node_id).filter((id): id is string => !!id)));
  const profileInject = profileInjectNodes.length ? profileInjectNodes.map(nodeLabel).join(', ') : 'every AI step';

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={7} lg={8}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Section title="What to learn" subtitle="Choose what the analyzer produces from your run history.">
          <FormControlLabel
            control={<Switch checked={settings.knowledge.enabled} onChange={(e) => update((s) => (s.knowledge.enabled = e.target.checked))} />}
            label="Build a knowledge base (patterns and mappings that worked)"
          />
          {settings.knowledge.enabled && (
            <Box sx={{ pl: 6, pb: 1 }}>
              <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 1.5 }}>
                Each analysis turns runs into entries, which start as Waiting. In-use entries are published to a knowledge category automatically,
                one snippet each. The Knowledge tab walks through every step.
              </Typography>

              <Typography variant="body2" fontWeight={600}>
                When a learning goes live
              </Typography>
              <FormControlLabel
                control={
                  <Switch size="small" checked={settings.knowledge.auto_publish} onChange={(e) => update((s) => (s.knowledge.auto_publish = e.target.checked))} />
                }
                label={
                  <>
                    Auto-approve learnings with enough evidence
                    <HelpTip title="Checked during each analysis: a Waiting entry that meets both numbers below goes In use. When off, every learning waits for you to approve it on the Knowledge tab. You can approve, pin or reject by hand either way." />
                  </>
                }
              />
              <Grid container spacing={2} sx={{ mt: 0.25, mb: 2 }}>
                <Grid item xs={12} sm={4}>
                  <NumberField
                    label="Min. supporting runs"
                    value={settings.knowledge.min_evidence}
                    min={1}
                    max={100}
                    disabled={!settings.knowledge.auto_publish}
                    onChange={(v) => update((s) => (s.knowledge.min_evidence = v))}
                    helperText="Before auto-approval"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <NumberField
                    label="Min. evidence strength"
                    suffix="%"
                    value={Math.round(settings.knowledge.min_confidence * 100)}
                    min={0}
                    max={100}
                    step={5}
                    disabled={!settings.knowledge.auto_publish}
                    onChange={(v) => update((s) => (s.knowledge.min_confidence = Number.isFinite(v) ? v / 100 : v))}
                    helperText="1 run ≈ 33%, 3 ≈ 60%, 8 ≈ 80%"
                  />
                </Grid>
                <Grid item xs={12} sm={4} sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="caption" color="text.secondary">
                    Both must be met. Strength grows with supporting runs, counts well-rated runs more, and for facts is reduced by runs that
                    contradict it.
                  </Typography>
                </Grid>
              </Grid>

              <Typography variant="body2" fontWeight={600}>
                How runs use it
              </Typography>
              <FormControlLabel
                control={<Switch size="small" checked={settings.knowledge.inject} onChange={(e) => update((s) => (s.knowledge.inject = e.target.checked))} />}
                label={
                  <>
                    Use the knowledge automatically when this runs
                    <HelpTip title="Relevant in-use entries are added to the AI's context on every run. Turn off to build the knowledge base without using it yet." />
                  </>
                }
              />
              <Grid container spacing={2} sx={{ mt: 0.25 }}>
                <Grid item xs={12} sm={4}>
                  <NumberField
                    label="Snippets per run"
                    value={settings.knowledge.top_k}
                    min={1}
                    max={20}
                    disabled={!settings.knowledge.inject}
                    onChange={(v) => update((s) => (s.knowledge.top_k = v))}
                    helperText="At most, per run"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <NumberField
                    label="Relevance cutoff"
                    suffix="%"
                    value={Math.round((settings.knowledge.min_similarity ?? 0.35) * 100)}
                    min={0}
                    max={95}
                    step={5}
                    disabled={!settings.knowledge.inject}
                    onChange={(v) => update((s) => (s.knowledge.min_similarity = Number.isFinite(v) ? v / 100 : v))}
                    helperText="Min. similarity for meaning-based matches"
                  />
                </Grid>
                <Grid item xs={12} sm={4} sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="caption" color="text.secondary">
                    Entries whose name appears in the input are always used. Others need to be at least this similar, so unrelated inputs get nothing
                    instead of noise. Try values with Knowledge → Test search.
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
          <FormControlLabel
            control={<Switch checked={settings.suggestions.enabled} onChange={(e) => update((s) => (s.suggestions.enabled = e.target.checked))} />}
            label="Suggest improvements to instructions and settings (you review before anything changes)"
          />
          <FormControlLabel
            control={<Switch checked={settings.insights.enabled} onChange={(e) => update((s) => (s.insights.enabled = e.target.checked))} />}
            label="Write a report: what works, what fails, gaps"
          />
        </Section>

        <Section title="When to run" subtitle="Scheduled analyses only look at runs they haven't analyzed before.">
          <RadioGroup value={settings.schedule.mode} onChange={(e) => update((s) => (s.schedule.mode = e.target.value as LearningSettings['schedule']['mode']))}>
            <FormControlLabel value="manual" control={<Radio />} label="Only when I click “Run analysis”" />
            <FormControlLabel value="interval" control={<Radio />} label="On a schedule (every few days)" />
            <FormControlLabel value="criteria" control={<Radio />} label="When enough new runs or feedback have arrived" />
          </RadioGroup>
          {settings.schedule.mode !== 'manual' && (
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              {settings.schedule.mode === 'interval' && (
                <Grid item xs={12} sm={4}>
                  <NumberField label="Every" suffix="days" value={settings.schedule.interval_days} min={1} max={90} onChange={(v) => update((s) => (s.schedule.interval_days = v))} />
                </Grid>
              )}
              {settings.schedule.mode === 'criteria' && (
                <>
                  <Grid item xs={12} sm={4}>
                    <NumberField
                      label="New runs"
                      value={settings.schedule.min_new_runs}
                      min={limits.min_runs}
                      onChange={(v) => update((s) => (s.schedule.min_new_runs = v))}
                      helperText="…or"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <NumberField
                      label="New feedback signals"
                      value={settings.schedule.min_new_signals}
                      min={1}
                      onChange={(v) => update((s) => (s.schedule.min_new_signals = v))}
                    />
                  </Grid>
                </>
              )}
              <Grid item xs={12} sm={4}>
                <NumberField
                  label="Never more often than every"
                  suffix="hours"
                  value={settings.schedule.min_hours_between}
                  min={1}
                  max={720}
                  onChange={(v) => update((s) => (s.schedule.min_hours_between = v))}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  Schedules are checked every 15–30 minutes, so an analysis may start a little after it's due.
                </Typography>
              </Grid>
            </Grid>
          )}
        </Section>

        <Section title="Budget" subtitle="Analysis is billed to your credits like any other run. A cycle stops at the cap and keeps whatever it learned so far.">
          <RadioGroup
            row
            value={capAuto ? 'auto' : 'custom'}
            onChange={(e) =>
              update((s) => {
                s.budget.credit_cap = e.target.value === 'auto' ? null : Math.max(plan?.recommended_cap || limits.min_credit_cap, limits.min_credit_cap);
              })
            }
          >
            <FormControlLabel value="auto" control={<Radio />} label="Automatic (recommended)" />
            <FormControlLabel value="custom" control={<Radio />} label="Custom cap" />
          </RadioGroup>
          {!capAuto && (
            <Box sx={{ maxWidth: 260, mt: 1 }}>
              <NumberField
                label="Max credits per cycle"
                value={settings.budget.credit_cap ?? NaN}
                min={limits.min_credit_cap}
                max={limits.max_credit_cap}
                onChange={(v) => update((s) => (s.budget.credit_cap = v))}
              />
            </Box>
          )}
          {plan && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              For the current sample: minimum {plan.min_cycle_credits.toFixed(2)}, recommended {plan.recommended_cap.toFixed(2)} credits
              {capAuto ? ` — automatic cap is ${plan.credit_cap.toFixed(2)}.` : '.'}
            </Typography>
          )}
        </Section>

        <Button
          onClick={() => setShowAdvanced(!showAdvanced)}
          endIcon={showAdvanced ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          sx={{ mb: 2 }}
        >
          {showAdvanced ? 'Hide advanced settings' : 'Show advanced settings'}
          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
            signals · sampling{targetType === 'chain' ? ' · steps' : ''} · analyzer models
          </Typography>
        </Button>

        {showAdvanced && (
        <>
        <Section
          title="How to judge runs"
          subtitle="Runs are ranked by the strongest signal available. Feedback helps but is never required — the AI quality check works on its own."
        >
          <Stack spacing={0.5}>
            {signals.map((p) => {
              const d = SIGNAL_DESCRIPTIONS[p] || { label: p, help: '' };
              const checked = settings.signals.enabled.includes(p);
              return (
                <Box key={p}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={checked}
                        onChange={(e) =>
                          update((s) => {
                            s.signals.enabled = e.target.checked ? [...s.signals.enabled, p] : s.signals.enabled.filter((x) => x !== p);
                          })
                        }
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          {d.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {d.help}
                        </Typography>
                      </Box>
                    }
                    sx={{ alignItems: 'flex-start', '& .MuiCheckbox-root': { pt: 0.5 } }}
                  />
                  {p === 'gcs_review_files' && checked && (
                    <TextField
                      size="small"
                      label="Storage prefix"
                      value={settings.signals.gcs_review_prefix || ''}
                      onChange={(e) => update((s) => (s.signals.gcs_review_prefix = e.target.value))}
                      sx={{ ml: 4, mt: 1, mb: 1, width: 360 }}
                    />
                  )}
                </Box>
              );
            })}
          </Stack>
        </Section>

        <Section title="Which runs to sample" subtitle="Each cycle only looks at runs it hasn't analyzed before, so a smaller sample regularly is usually better than one big pass.">
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <NumberField
                label="Look back"
                suffix="days"
                value={settings.sampling.window_days}
                min={1}
                max={365}
                onChange={(v) => update((s) => (s.sampling.window_days = v))}
                helperText={
                  `Unseen entries go stale after ${Number.isFinite(settings.sampling.window_days) ? settings.sampling.window_days * 3 : '3×'} days` +
                  (status.retention_days ? ` · history kept ${status.retention_days} days` : '')
                }
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <Typography variant="body2" gutterBottom>
                Sample {settings.sampling.sample_percent}% of new runs
              </Typography>
              <Slider
                value={settings.sampling.sample_percent}
                min={1}
                max={100}
                onChange={(_, v) => update((s) => (s.sampling.sample_percent = v as number))}
                valueLabelDisplay="auto"
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <NumberField
                label="At least"
                suffix="runs"
                value={settings.sampling.min_runs}
                min={limits.min_runs}
                max={500}
                onChange={(v) => update((s) => (s.sampling.min_runs = v))}
                helperText="Skips the cycle if fewer are available"
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <NumberField
                label="At most"
                suffix="runs"
                value={settings.sampling.max_runs}
                min={settings.sampling.min_runs}
                max={limits.max_runs}
                onChange={(v) => update((s) => (s.sampling.max_runs = v))}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <NumberField
                label="Max characters per run"
                value={settings.sampling.max_chars_per_run}
                min={1000}
                max={20000}
                step={500}
                onChange={(v) => update((s) => (s.sampling.max_chars_per_run = v))}
                helperText="Longer inputs/outputs are trimmed"
              />
            </Grid>
          </Grid>
          <Stack sx={{ mt: 1 }}>
            <FormControlLabel
              control={<Switch size="small" checked={settings.sampling.success_only} onChange={(e) => update((s) => (s.sampling.success_only = e.target.checked))} />}
              label="Only successful runs"
            />
            <FormControlLabel
              control={
                <Switch size="small" checked={settings.sampling.only_with_signals} onChange={(e) => update((s) => (s.sampling.only_with_signals = e.target.checked))} />
              }
              label="Only runs that have a signal (feedback, failure, rerun…)"
            />
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={settings.sampling.rescan_on_signal_upgrade}
                  onChange={(e) => update((s) => (s.sampling.rescan_on_signal_upgrade = e.target.checked))}
                />
              }
              label="Re-analyze a run when new feedback arrives for it"
            />
          </Stack>
        </Section>

        {targetType === 'chain' && nodes.length > 0 && (
          <Section
            title="Step overrides (optional)"
            subtitle="The learning profile already picks which steps learning reads and which steps get knowledge, and sets what each of those steps searches for (Profile → Steps that get knowledge). Use these fields only to override the steps; leave them empty to use the profile. The Knowledge tab shows exactly what each step receives."
          >
            <Autocomplete
              multiple
              size="small"
              options={nodes.map((n) => n.node_id)}
              value={settings.scope.learn_nodes}
              getOptionLabel={nodeLabel}
              onChange={(_, v) => update((s) => (s.scope.learn_nodes = v))}
              renderTags={(v, getTagProps) => v.map((id, i) => <Chip size="small" label={nodeLabel(id)} {...getTagProps({ index: i })} />)}
              renderInput={(p) => (
                <TextField
                  {...p}
                  label="Learn only from these steps"
                  placeholder={settings.scope.learn_nodes.length ? undefined : 'Follow the profile'}
                  helperText={settings.scope.learn_nodes.length ? undefined : `Empty, so following the profile: ${profileLearn}`}
                />
              )}
              sx={{ mb: 2 }}
            />
            <Autocomplete
              multiple
              size="small"
              options={aiNodes.map((n) => n.node_id)}
              value={settings.scope.inject_nodes}
              getOptionLabel={nodeLabel}
              onChange={(_, v) => update((s) => (s.scope.inject_nodes = v))}
              renderTags={(v, getTagProps) => v.map((id, i) => <Chip size="small" label={nodeLabel(id)} {...getTagProps({ index: i })} />)}
              renderInput={(p) => (
                <TextField
                  {...p}
                  label="Give knowledge only to these AI steps"
                  placeholder={settings.scope.inject_nodes.length ? undefined : 'Follow the profile'}
                  helperText={
                    settings.scope.inject_nodes.length
                      ? 'Overriding the profile. Each step still searches with its query from the profile, or with its whole input if the profile has none for it.'
                      : `Empty, so following the profile: ${profileInject}`
                  }
                />
              )}
            />
          </Section>
        )}

        <Section
          title="Analyzer models"
          subtitle={
            status.allow_model_override
              ? `Each stage uses the platform default unless you pick a model for this ${targetType}. Run analysis is most of the cost, so a fast, cheap model is usually right there; a stronger review model gives better suggestions.`
              : 'Models are set by your platform administrator.'
          }
        >
          <Stack spacing={2} divider={<Divider flexItem />}>
            {MODEL_STAGES.map(({ stage, label, help }) => {
              const platformDefault = status.models?.[stage] || null;
              const custom = settings.models[stage] ?? null;
              return (
                <Grid container spacing={2} key={stage} alignItems="center">
                  <Grid item xs={12} sm={5}>
                    <Typography variant="body2" fontWeight={500}>
                      {label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {help}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={7}>
                    {status.allow_model_override ? (
                      <Stack spacing={1}>
                        <RadioGroup
                          row
                          value={custom !== null ? 'custom' : 'default'}
                          onChange={(e) => update((s) => (s.models[stage] = e.target.value === 'default' ? null : platformDefault ?? ''))}
                        >
                          <FormControlLabel
                            value="default"
                            control={<Radio size="small" />}
                            label={<Typography variant="body2">Platform default{platformDefault ? ` (${platformDefault})` : ''}</Typography>}
                          />
                          <FormControlLabel value="custom" control={<Radio size="small" />} label={<Typography variant="body2">Choose a model</Typography>} />
                        </RadioGroup>
                        {custom !== null && (
                          <ModelSelector size="small" fullWidth value={custom} onChange={(v) => update((s) => (s.models[stage] = (v as string) ?? ''))} />
                        )}
                      </Stack>
                    ) : (
                      <Chip size="small" variant="outlined" label={platformDefault || 'Platform default'} />
                    )}
                  </Grid>
                </Grid>
              );
            })}
          </Stack>
        </Section>
        </>
        )}

        <Stack direction="row" spacing={1} sx={{ position: 'sticky', bottom: 0, py: 1.5, bgcolor: 'background.default', zIndex: 1 }} alignItems="center">
          <Button variant="contained" onClick={save} disabled={!dirty || saving}>
            {saving ? 'Saving…' : 'Save settings'}
          </Button>
          <Button onClick={() => setSettings(clone(detail.settings))} disabled={!dirty || saving}>
            Discard changes
          </Button>
          {status.defaults && (
            <Button color="inherit" onClick={() => update((s) => Object.assign(s, clone(status.defaults!)))} disabled={saving}>
              Reset to defaults
            </Button>
          )}
          {savedMsg && (
            <Typography variant="body2" color="success.main">
              {savedMsg}
            </Typography>
          )}
        </Stack>
      </Grid>

      <Grid item xs={12} md={5} lg={4}>
        <Paper variant="outlined" sx={{ p: 2.5, position: { md: 'sticky' }, top: { md: 16 } }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              Next cycle preview
            </Typography>
            {planLoading && <CircularProgress size={16} />}
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            Updates as you change settings{dirty ? ' (unsaved)' : ''}.
          </Typography>
          <Divider sx={{ mb: 1.5 }} />
          {plan ? <PlanSummary plan={plan} /> : <Typography color="text.secondary">Calculating…</Typography>}
        </Paper>
      </Grid>
    </Grid>
  );
};
